export interface KpiValues {
  [key: string]: any;

  year3Revenue?: number;
  year3YieldOnCost?: number;
  projectIRR?: number;
  totalInvestment?: number;
}

export type Json =
  | null
  | boolean
  | number
  | string
  | Json[]
  | { [key: string]: Json };

export function n(v: any, def = 0): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : def;
}

export function div(a: any, b: any, def = 0): number {
  const A = n(a, 0);
  const B = n(b, 0);
  return B === 0 ? def : A / B;
}

export function pct(p: any): number {
  return n(p, 0) / 100;
}

export function sum(arr: any[] = []): number {
  return arr.reduce((acc, v) => acc + n(v, 0), 0);
}

export function toFixedSafe(v: any, digits = 0): number {
  const x = n(v, 0);
  return n(Number(x.toFixed(digits)), 0);
}

export function round2(v: any): number {
  return toFixedSafe(v, 2);
}

export function revpar(adr: any, occPct: any): number {
  return n(adr, 0) * pct(occPct);
}

export function perRoom(total: any, rooms: any): number {
  return div(total, rooms, 0);
}

export function perSqm(total: any, sqm: any): number {
  return div(total, sqm, 0);
}

export function percentOfTotal(part: any, total: any): number {
  const T = n(total, 0);
  return T === 0 ? 0 : (n(part, 0) / T) * 100;
}

export type RoomMixItem = {
  id?: string;
  name?: string;
  rooms?: number; // legacy name
  count?: number; // alternative
  adr?: number; // optional per-type ADR (advanced mode)
  adrWeight?: number; // optional 0..100 (% contribution to ADR from base)
};

export function roomsMix(items: RoomMixItem[] = []): {
  items: Required<RoomMixItem>[];
  total: number;
} {
  const arr: RoomMixItem[] = Array.isArray(items) ? items : [];
  const normalized = (arr || []).map((it, i) => {
    const r = n((it as any).rooms ?? (it as any).count ?? 0, 0);
    return {
      id: String(it.id ?? `rt${i + 1}`),
      name: String(it.name ?? `Type ${i + 1}`),
      rooms: r,
      count: r,
      adr: n((it as any).adr ?? NaN, NaN),
      adrWeight: n((it as any).adrWeight ?? 100, 100),
    };
  });
  const total = sum(normalized.map((x) => x.rooms));
  return { items: normalized as Required<RoomMixItem>[], total: n(total, 0) };
}

export function roomsTotal(items: RoomMixItem[] = []): number {
  return roomsMix(items).total;
}

export function roomsCount(items: RoomMixItem[] = []): number {
  return roomsTotal(items);
}

function _weightedADR_calc(
  baseADR: number | undefined,
  items: RoomMixItem[] = [],
): number {
  const { items: mix, total } = roomsMix(items);
  if (total === 0) return n(baseADR, 0);

  // Case A: explicit per-type ADRs
  const hasPerTypeAdr = mix.some((m) => Number.isFinite(m.adr));
  if (hasPerTypeAdr) {
    const totalWeighted = sum(mix.map((m) => n(m.adr, 0) * n(m.rooms, 0)));
    return div(totalWeighted, total, n(baseADR, 0));
  }

  // Case B: scale base ADR by adrWeight (0..100)
  const b = n(baseADR, 0);
  const weightSum = sum(mix.map((m) => n(m.adrWeight, 100) * n(m.rooms, 0)));
  const avgWeight = div(weightSum, total * 100, 1);
  return b * avgWeight;
}

export function weightedADR(a: any, b?: any): number {
  if (Array.isArray(a))
    return _weightedADR_calc(n(b, undefined), a as RoomMixItem[]);
  return _weightedADR_calc(n(a, undefined), (b as RoomMixItem[]) || []);
}

export const mixWeightedADR = weightedADR;

export function roomsAvailable(rooms: any, days: any): number {
  return n(rooms, 0) * n(days, 0);
}

export function roomsSold(roomsAvail: any, occPct: any): number {
  return roomsAvail * pct(occPct);
}

export function adrForMonth(v: any): number {
  return n(v, 0);
}

export function occForMonth(p: any): number {
  return n(p, 0);
}

export function monthlyRevpar(adr: any, occPct: any): number {
  return revpar(adr, occPct);
}

export function avgADR(values: any[] = []): number {
  const valid = values.map((v) => n(v, 0));
  return div(sum(valid), valid.length || 1, 0);
}

export function avgOcc(values: any[] = []): number {
  const valid = values.map((v) => n(v, 0));
  return div(sum(valid), valid.length || 1, 0);
}

export type BudgetStatus = "ok" | "warning" | "over";

export function budgetStatus(
  actual?: number | null,
  target?: number | null,
  tolerancePct = 0.05,
): BudgetStatus {
  const a = n(actual, NaN);
  const t = n(target, NaN);
  if (!Number.isFinite(a) || !Number.isFinite(t) || t === 0) return "ok";
  const diff = (a - t) / t;
  if (diff > tolerancePct) return "over";
  if (diff < -tolerancePct) return "warning";
  return "ok";
}

export function normalizeRoomRevenue(input: any): any {
  if (!input) return { monthly: Array(12).fill({ adr: 0, occ: 0 }) };

  if (input.monthly && Array.isArray(input.monthly)) {
    return input;
  }

  if (
    input.adr &&
    input.occ &&
    Array.isArray(input.adr) &&
    Array.isArray(input.occ)
  ) {
    return {
      monthly: input.adr.map((adr: number, i: number) => ({
        adr: n(adr, 0),
        occ: n(input.occ[i], 0),
      })),
    };
  }

  if (Array.isArray(input)) {
    return { monthly: input };
  }

  return { monthly: Array(12).fill({ adr: 0, occ: 0 }) };
}

export function normalizeFacilities(input: any): string[] {
  if (!input) return [];

  if (Array.isArray(input)) {
    return input.filter((f) => typeof f === "string");
  }

  if (typeof input === "object" && !Array.isArray(input)) {
    return Object.entries(input)
      .filter(([_, value]) => value === true)
      .map(([key, _]) => key);
  }

  return [];
}

export function hasValidRoomRevenue(roomRevenue: any): boolean {
  if (!roomRevenue) return false;

  if (roomRevenue.monthly && Array.isArray(roomRevenue.monthly)) {
    return roomRevenue.monthly.some(
      (m: any) => (m.adr && m.adr > 0) || (m.occ && m.occ > 0),
    );
  }

  if (roomRevenue.adr && Array.isArray(roomRevenue.adr)) {
    return (
      roomRevenue.adr.some((v: number) => v > 0) ||
      (roomRevenue.occ && roomRevenue.occ.some((v: number) => v > 0))
    );
  }

  return false;
}

export function deepMerge<
  T extends Record<string, any>,
  U extends Record<string, any>,
>(priority: T, fallback: U): T & U {
  const result: any = Array.isArray(priority)
    ? [...priority]
    : { ...(priority || {}) };

  if (!fallback || typeof fallback !== "object") {
    return result;
  }

  for (const [key, fallbackValue] of Object.entries(fallback)) {
    const priorityValue = result[key];

    if (priorityValue !== undefined && priorityValue !== null) {
      if (key === "facilities") {
        if (Array.isArray(priorityValue) && priorityValue.length > 0) {
          console.info(
            "[deepMerge] Preserving priority facilities:",
            priorityValue,
          );
          continue;
        }
      } else if (Array.isArray(priorityValue) && priorityValue.length > 0) {
        continue;
      } else if (
        priorityValue &&
        typeof priorityValue === "object" &&
        !Array.isArray(priorityValue) &&
        fallbackValue &&
        typeof fallbackValue === "object" &&
        !Array.isArray(fallbackValue)
      ) {
        result[key] = deepMerge(priorityValue, fallbackValue);
        continue;
      } else if (
        typeof priorityValue === "number" ||
        typeof priorityValue === "string" ||
        typeof priorityValue === "boolean"
      ) {
        continue;
      }
    }

    if (key === "facilities") {
      if (Array.isArray(fallbackValue) && fallbackValue.length > 0) {
        result[key] = fallbackValue;
      }
    } else if (Array.isArray(fallbackValue)) {
      if (fallbackValue.length > 0 || !Array.isArray(priorityValue)) {
        result[key] = fallbackValue;
      }
    } else if (fallbackValue && typeof fallbackValue === "object") {
      if (
        priorityValue &&
        typeof priorityValue === "object" &&
        !Array.isArray(priorityValue)
      ) {
        result[key] = deepMerge(priorityValue, fallbackValue);
      } else {
        result[key] = fallbackValue;
      }
    } else {
      result[key] = fallbackValue;
    }
  }

  return result;
}

export function currencyOrDefault(cur: any, fallback = "EUR"): string {
  const s = String(cur ?? "").trim();
  return s || fallback;
}

export const kpiUtils = {
  n,
  div,
  pct,
  sum,
  toFixedSafe,
  round2,
  revpar,
  perRoom,
  perSqm,
  percentOfTotal,
  roomsMix,
  roomsTotal,
  roomsCount,
  weightedADR,
  mixWeightedADR,
  roomsAvailable,
  roomsSold,
  adrForMonth,
  occForMonth,
  monthlyRevpar,
  avgADR,
  avgOcc,
  budgetStatus,
  deepMerge,
  currencyOrDefault,
};

export default kpiUtils;

export function toArray<T = any>(v: any): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

export function getDeep(obj: any, path: string): any {
  if (!obj) return undefined;
  return path
    .split(".")
    .reduce((acc, key) => (acc ? acc[key] : undefined), obj);
}

export function setDeep(obj: any, path: string, value: any): any {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    if (typeof cur[k] !== "object" || cur[k] === null) cur[k] = {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
  return obj;
}

/**
 * Ensure an array exists at the given path, but preserve existing data
 * Only creates empty arrays when value is truly undefined/null
 */
export function ensureArrayAt(obj: any, path: string): any {
  const v = getDeep(obj, path);

  // If already an array, leave it alone (even if empty)
  if (Array.isArray(v)) {
    return obj;
  }

  // Special handling for facilities - check multiple locations
  if (path === "facilities") {
    const propertyFacilities = getDeep(obj, "property.facilities");
    const topLevelFacilities = obj.facilities;

    if (Array.isArray(propertyFacilities) && propertyFacilities.length > 0) {
      setDeep(obj, path, propertyFacilities);
      console.info(
        "[ensureArrayAt] Recovered facilities from property.facilities:",
        propertyFacilities,
      );
    } else if (
      Array.isArray(topLevelFacilities) &&
      topLevelFacilities.length > 0
    ) {
      setDeep(obj, path, topLevelFacilities);
    } else if (v === undefined || v === null) {
      setDeep(obj, path, []);
    } else {
      console.warn(`[ensureArrayAt] Expected array at ${path}, found:`, v);
      if (typeof v === "string") {
        setDeep(obj, path, [v]);
      } else if (typeof v === "object") {
        const facilityArray = Object.entries(v)
          .filter(([_, val]) => val === true)
          .map(([key, _]) => key);
        setDeep(obj, path, facilityArray);
      }
    }
    return obj;
  }

  // For room revenue paths, be extra careful
  if (path.includes("roomRevenue") || path.includes("roomRev")) {
    if (v !== undefined && v !== null && !Array.isArray(v)) {
      console.warn(
        `[ensureArrayAt] Expected array at ${path}, preserving existing value:`,
        v,
      );
      return obj;
    }
  }

  // For other paths, only create empty array if truly undefined/null
  if (v === undefined || v === null) {
    setDeep(obj, path, []);
  } else if (!Array.isArray(v)) {
    console.warn(
      `[ensureArrayAt] Expected array at ${path}, found:`,
      typeof v,
      "- preserving existing value",
    );
  }

  return obj;
}

export function normalizeKpis<K extends Record<string, any>>(
  kpis: K | null | undefined,
): K {
  const k: any = kpis && typeof kpis === "object" ? { ...kpis } : {};

  const existingFacilities = k.facilities;
  const propertyFacilities = k.property?.facilities;

  if (
    !existingFacilities ||
    (Array.isArray(existingFacilities) && existingFacilities.length === 0)
  ) {
    if (
      propertyFacilities &&
      Array.isArray(propertyFacilities) &&
      propertyFacilities.length > 0
    ) {
      k.facilities = propertyFacilities;
      console.info(
        "[normalizeKpis] Recovered facilities from property.facilities:",
        propertyFacilities,
      );
    } else {
      ensureArrayAt(k, "facilities");
    }
  } else if (!Array.isArray(existingFacilities)) {
    if (typeof existingFacilities === "object") {
      k.facilities = Object.entries(existingFacilities)
        .filter(([_, val]) => val === true)
        .map(([key, _]) => key);
      console.info(
        "[normalizeKpis] Converted facilities object to array:",
        k.facilities,
      );
    } else {
      ensureArrayAt(k, "facilities");
    }
  }

  if (!k.roomMix) {
    k.roomMix = [];
  } else if (Array.isArray(k.roomMix)) {
    k.roomMix = k.roomMix.map((item: any) => ({
      id: item.id || crypto.randomUUID(),
      name: item.name || item.type || "",
      rooms: Number(item.rooms ?? item.count ?? 0),
      adrWeight: Number(item.adrWeight ?? 100),
    }));
  } else if (typeof k.roomMix === "object" && Array.isArray(k.roomMix.types)) {
    k.roomMix = k.roomMix.types.map((item: any) => ({
      id: item.id || crypto.randomUUID(),
      name: item.name || item.type || "",
      rooms: Number(item.rooms ?? item.count ?? 0),
      adrWeight: Number(item.adrWeight ?? 100),
    }));
  } else {
    k.roomMix = [];
  }

  ensureArrayAt(k, "operatingCosts.lines");
  ensureArrayAt(k, "payroll.advanced.roles");

  const roomRevPaths = [
    "roomRevenue.monthly.adr",
    "roomRevenue.monthly.occ",
    "roomRev.adr",
    "roomRev.occ",
  ];

  for (const path of roomRevPaths) {
    const existing = getDeep(k, path);
    if (existing === undefined || existing === null) {
      ensureArrayAt(k, path);
    } else if (!Array.isArray(existing)) {
      console.warn(
        `[normalizeKpis] Room revenue data at ${path} is not an array, preserving:`,
        existing,
      );
    }
  }

  ensureArrayAt(k, "otherRevenue.monthly.treatmentsPerDay");
  ensureArrayAt(k, "otherRevenue.monthly.avgPricePerTreatment");

  return k as K;
}

export function sumRooms(arr: any): number {
  const a = Array.isArray(arr) ? arr : [];
  return a.reduce((acc, it) => acc + n(it && (it.rooms ?? it.count), 0), 0);
}

export function computeRoomsFromKpis(k: any): number {
  if (!k) return 0;

  if (k.rooms?.total != null && Number.isFinite(k.rooms.total)) {
    return n(k.rooms.total, 0);
  }

  if (Number.isFinite(k.roomsTotal)) return n(k.roomsTotal, 0);

  if (Number.isFinite(k.rooms)) return n(k.rooms, 0);

  const fromRoomTypes = sumRooms(k.roomTypes);
  if (fromRoomTypes > 0) return fromRoomTypes;

  const fromRoomMix = sumRooms(k.roomMix);
  if (fromRoomMix > 0) return fromRoomMix;

  const fromMixTypes = sumRooms(k.roomMix && k.roomMix.types);
  return fromMixTypes;
}

export function pickPurchasePrice(k: any): number {
  if (!k) return 0;
  const fromSection = n(k.purchase?.price, NaN);
  if (Number.isFinite(fromSection)) return fromSection;
  return n(k.purchasePrice, 0);
}

export { lastSavedLabel } from "./utils";
